import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Activity, TrendingUp } from "lucide-react";

/**
 * SystemHealth — compact card surfacing the three worst-performing
 * tracked routes over the current in-process rolling window.
 *
 * Owner-only (endpoint is `require_superuser`). Polls every 60s.
 * Colour-coded per SLO drift-budget (from PERFORMANCE_BASELINE.md):
 *   green   p95 ≤ baseline
 *   amber   baseline < p95 ≤ baseline × 1.20
 *   red     p95 > baseline × 1.20
 */
const SLO = {
  "/api/auth/login": 400,
  "/api/auth/me": 80,
  "/api/dashboard": 500,
  "/api/risks": 300,
  "/api/exec/ai-briefing": 8000,
  "/api/exec/ai-briefing/:id": 100,
  "/api/exec/ai-briefing/ledger": 250,
  "/api/copilot/ask": 6000,
  "/api/auth/sessions": 120,
  "/api/healthz": 40,
};
const DRIFT = 0.20;

const _band = (p95, baseline) => {
  if (!baseline || p95 == null) return "neutral";
  if (p95 <= baseline) return "green";
  if (p95 <= baseline * (1 + DRIFT)) return "amber";
  return "red";
};
const _pillClass = {
  green: "bg-emerald-100 text-emerald-800",
  amber: "bg-amber-100 text-amber-800",
  red: "bg-red-100 text-red-800",
  neutral: "bg-black/5 text-ek-ink/60",
};

export const SystemHealth = () => {
  const [routes, setRoutes] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/healthz/perf");
        if (!cancelled) { setRoutes(data.routes || {}); setErr(false); }
      } catch {
        if (!cancelled) setErr(true);
      }
    };
    load();
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (err) return null;
  if (!routes) return null;

  // Rank tracked routes by drift ratio; take worst 3 with enough samples.
  const tracked = Object.keys(SLO)
    .map((r) => ({ route: r, baseline: SLO[r], ...(routes[r] || {}) }))
    .filter((x) => x.samples >= 5 && x.p95 != null)
    .map((x) => ({ ...x, ratio: x.p95 / x.baseline }))
    .sort((a, b) => b.ratio - a.ratio)
    .slice(0, 3);

  if (tracked.length === 0) return null;

  return (
    <div className="rounded-xl border border-black/5 bg-white p-5" data-testid="system-health-widget">
      <div className="flex items-center gap-2">
        <Activity size={14} className="text-ek-ink/50" />
        <p className="text-[10px] font-semibold uppercase tracking-wide text-ek-ink/55">System health</p>
      </div>
      <p className="mt-0.5 text-xs text-ek-ink/50">Live p95 latency vs SLO. Updated every 60s.</p>
      <ul className="mt-3 space-y-2">
        {tracked.map((t) => {
          const band = _band(t.p95, t.baseline);
          return (
            <li
              key={t.route}
              className="flex items-center justify-between gap-3"
              data-testid={`system-health-row-${t.route.replace(/[^a-z0-9]/gi, "-")}`}
            >
              <span className="min-w-0 truncate font-mono text-[11px] text-ek-ink/75" title={t.route}>
                {t.route}
              </span>
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${_pillClass[band]}`}
                data-testid={`system-health-band-${band}`}
              >
                <TrendingUp size={10} /> {t.p95}ms · SLO {t.baseline}ms
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
